import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip } from 'recharts';
import { Download, Send, CheckCircle2, Eye, MousePointer, MessageSquare, AlertTriangle, TrendingUp, Calendar, GitBranch, HelpCircle, UserMinus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type LucideIcon } from 'lucide-react';
import { useAnalytics, useAnalyticsBestSendingTimes, type AnalyticsPeriod, type AnalyticsTopSegment, type AnalyticsABTest } from '@/hooks/useAnalytics';
import { STORAGE, API_BASE_URL } from '@/_config';
import { useAuth } from '@/context/AuthContext';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

interface KpiItem {
  label: string;
  value: string;
  icon: LucideIcon;
  color: string;
  change: string;
  tooltip: string;
  invertChange?: boolean;
}

const ranges = ['Last 7 days', 'Last 30 days', 'Last 90 days'];

const periodMap: Record<string, AnalyticsPeriod> = {
  'Last 7 days': '7d',
  'Last 30 days': '30d',
  'Last 90 days': '90d',
};

const CIRC = 503; // SVG donut circle circumference (2π × r=80 ≈ 502.65)

function fmtChange(v: number): string {
  return v >= 0 ? `+${v}%` : `${v}%`;
}

function fmtCountChange(v: number): string {
  return v >= 0 ? `+${v}` : `${v}`;
}

function fmtPct(v: number): string {
  return `${v}%`;
}


function heatColor(pct: number | null): { bg: string; text: string } {
  if (pct === null) return { bg: 'var(--gray-100)', text: 'var(--gray-400)' };
  if (pct >= 80)   return { bg: '#1e5fcc', text: '#fff' };
  if (pct >= 65)   return { bg: '#2e7de8', text: '#fff' };
  if (pct >= 50)   return { bg: '#5ba3ef', text: '#fff' };
  if (pct >= 35)   return { bg: '#94c5f7', text: '#1e3a5f' };
  return { bg: '#d0e8fc', text: '#1e3a5f' };
}

function ColTip({ label, tip }: { label: string; tip: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle size={11} style={{ color: 'var(--gray-400)', cursor: 'help', flexShrink: 0 }} />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
          {tip}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

export default function AnalyticsPage() {
  const [dateRange, setDateRange] = useState('Last 30 days');
  const period = periodMap[dateRange] ?? '30d';
  const { data: queryData, isLoading } = useAnalytics(period);
  const { data: bstData, isLoading: bstLoading } = useAnalyticsBestSendingTimes(period);
  const { user } = useAuth();
  const analytics = queryData?.data;
  const bestSendingTimes = bstData?.data;

  // -------------------------------------------------------------------------
  // KPI cards
  // -------------------------------------------------------------------------
  const m = analytics?.metrics;
  const ch = m?.changes;

  const kpis: KpiItem[] = [
    {
      label: 'Total Sent',
      value: isLoading ? '—' : (m?.total_sent ?? 0).toLocaleString(),
      icon: Send,
      color: 'var(--navy)',
      change: isLoading ? '—' : fmtChange(ch?.delivery_rate ?? 0),
      tooltip: 'Total number of emails dispatched during this period across all campaigns and sequences.',
    },
    {
      label: 'Delivery Rate',
      value: isLoading ? '—' : fmtPct(m?.delivery_rate ?? 0),
      icon: CheckCircle2,
      color: 'var(--sky)',
      change: isLoading ? '—' : fmtChange(ch?.delivery_rate ?? 0),
      tooltip: 'Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100',
    },
    {
      label: 'Not Reply Rate',
      value: isLoading ? '—' : fmtPct(m?.open_rate ?? 0),
      icon: Eye,
      color: 'var(--amber)',
      change: isLoading ? '—' : fmtChange(ch?.open_rate ?? 0),
      tooltip: 'Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100',
    },
    {
      label: 'Viewed Rate',
      value: isLoading ? '—' : fmtPct(m?.click_rate ?? 0),
      icon: MousePointer,
      color: 'var(--green)',
      change: isLoading ? '—' : fmtChange(ch?.click_rate ?? 0),
      tooltip: 'Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100',
    },
    {
      label: 'Reply Rate',
      value: isLoading ? '—' : fmtPct(m?.reply_rate ?? 0),
      icon: MessageSquare,
      color: 'var(--sky)',
      change: isLoading ? '—' : fmtChange(ch?.reply_rate ?? 0),
      tooltip: 'Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100',
    },
    {
      label: 'Bounce Rate',
      value: isLoading ? '—' : fmtPct(m?.bounce_rate ?? 0),
      icon: AlertTriangle,
      color: 'var(--red)',
      change: isLoading ? '—' : fmtChange(ch?.bounce_rate ?? 0),
      tooltip: 'Percentage of emails that failed to deliver or were flagged as spam. Lower is better.\nFormula: (Bounced + Complained) ÷ Total Sent × 100',
    },
    {
      label: 'Unsubscribed',
      value: isLoading ? '—' : (m?.unsubscribed_count ?? 0).toLocaleString(),
      icon: UserMinus,
      color: 'var(--amber)',
      change: isLoading ? '—' : fmtCountChange(ch?.unsubscribed_count ?? 0),
      tooltip: 'Number of recipients who unsubscribed from your emails during this period.',
      invertChange: true,
    },
  ];

  // -------------------------------------------------------------------------
  // Performance chart
  // -------------------------------------------------------------------------
  const timeSeries = analytics?.time_series ?? [];

  // -------------------------------------------------------------------------
  // Delivery health donut — uses dedicated delivery_health API field
  // -------------------------------------------------------------------------
  const dh             = analytics?.delivery_health;
  const deliveredPct   = dh?.delivered_pct   ?? 0;
  const softBouncePct  = dh?.soft_bounce_pct ?? 0;
  const hardBouncePct  = dh?.hard_bounce_pct ?? 0;
  const deliveredLen   = Math.round((deliveredPct  / 100) * CIRC);
  const softBounceLen  = Math.round((softBouncePct / 100) * CIRC);
  const hardBounceLen  = Math.round((hardBouncePct / 100) * CIRC);

  // -------------------------------------------------------------------------
  // Plan feature gates
  // -------------------------------------------------------------------------
  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);
  const isPlanDeactivated =
    user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';
  const canExport =
    !!(user?.organization?.plan_features?.analytics_export) && !isTrialExpired && !isPlanDeactivated;
  const hasAdvancedAccess =
    !!(user?.organization?.plan_features?.advanced_analytics) &&
    !isTrialExpired &&
    !isPlanDeactivated;

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------
  const handleExport = async () => {
    try {
      const raw = localStorage.getItem(STORAGE);
      const token: string | undefined = raw ? (JSON.parse(raw) as any)?.access_token ?? (JSON.parse(raw) as any)?.token : undefined;
      const response = await fetch(`${API_BASE_URL}analytics/export?period=${period}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-logs-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently ignore export errors
    }
  };

  // -------------------------------------------------------------------------
  // Top campaigns / sequences / segments
  // -------------------------------------------------------------------------
  const topCampaigns = analytics?.top_campaigns ?? [];
  const topSequences = analytics?.top_sequences ?? [];
  const topSegments: AnalyticsTopSegment[] = analytics?.top_segments ?? [];
  const abTests: AnalyticsABTest[] = analytics?.ab_tests ?? [];
  const sequenceFunnel = analytics?.sequence_funnel;
  const hasNoData = !isLoading && (m?.total_sent ?? 0) === 0;

  return (
    <TooltipProvider>
    <div className="portal-page an-page">
      <div className="an-header">
        <h1 className="an-title">Analytics</h1>
        <div className="an-controls">
          <div className="an-date-toggle">
            {ranges.map(r => (
              <button key={r} onClick={() => setDateRange(r)} className={`an-date-btn ${dateRange === r ? 'an-date-btn-active' : ''}`}>
                {r}
              </button>
            ))}
          </div>
          {canExport && (
            <button className="btn btn-secondary btn-sm" onClick={handleExport}><Download size={16} /> Export Report</button>
          )}
        </div>
      </div>

      {hasNoData ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{
            width: 120, height: 120, borderRadius: '50%',
            background: '#dbeeff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <TrendingUp size={36} color="var(--sky)" />
          </div>
          <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
            No analytics data for this period
          </p>
          <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 24 }}>
            Try selecting a different date range, or send your first campaign
          </p>
          <Link to="/app/campaigns/new" className="btn btn-primary btn-sm">
            Create a Campaign
          </Link>
        </div>
      ) : (
      <>
      <div className="an-kpi-grid">
          {kpis.map(kpi => (
            <Tooltip key={kpi.label}>
              <TooltipTrigger asChild>
                <div className="card" style={{ textAlign: 'center', cursor: 'default' }}>
                  <kpi.icon size={22} color={kpi.color} style={{ marginBottom: 8 }} />
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 500, marginBottom: 4 }}>{kpi.label}</p>
                  <p style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.2 }}>{kpi.value}</p>
                  <p style={{ fontSize: 12, color: kpi.change === '—' ? 'var(--gray-400)' : kpi.invertChange ? (kpi.change.startsWith('+') ? 'var(--red)' : 'var(--green)') : (kpi.change.startsWith('+') ? 'var(--green)' : kpi.label === 'Bounce Rate' ? 'var(--green)' : 'var(--red)'), fontWeight: 500, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <TrendingUp size={12} /> {kpi.change}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                {kpi.tooltip}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

      {!hasAdvancedAccess && (
        <div className="card" style={{ textAlign: 'center', padding: '32px 24px', marginBottom: 32 }}>
          <p style={{ fontSize: 15, color: 'var(--navy)', fontWeight: 600, marginBottom: 8 }}>
            Advanced Analytics require a Gold or Platinum plan
          </p>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20 }}>
            Upgrade to unlock performance charts, campaign breakdowns, delivery health, and sequence insights.
          </p>
          <a href="/app/upgrade-plan" className="btn btn-primary btn-sm">View Plans</a>
        </div>
      )}

      {hasAdvancedAccess && (
      <>
      <div className="card" style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Performance Overview</h3>
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: 20, fontSize: 12, justifyContent: 'flex-end', marginBottom: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 3, background: 'var(--sky)', borderRadius: 2, display: 'inline-block' }} /> Not Reply Rate</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 3, background: 'var(--amber)', borderRadius: 2, display: 'inline-block' }} /> Viewed Rate</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeSeries} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: 'var(--gray-500)' }}
                tickLine={false}
                axisLine={false}
                interval={timeSeries.length <= 7 ? 0 : timeSeries.length <= 31 ? 4 : 9}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--gray-500)' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v === 0 ? '0' : String(v)}
              />
              <ChartTooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid var(--gray-200)' }}
                formatter={(value: number, name: string) => [value, name === 'opened' ? 'Not Reply Rate' : 'Viewed Rate']}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              />
              <Line type="monotone" dataKey="opened" name="opened" stroke="var(--sky)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="clicked" name="clicked" stroke="var(--amber)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="an-grid">
        <div className="card">
          <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Top Campaigns</h3>
          <div className="an-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 500 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Campaign</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Sent" tip="Total emails dispatched for this campaign during the selected period." /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Delivered" tip={"Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100"} /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Not Replied Yet" tip={"Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100"} /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Viewed" tip={"Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100"} /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Replied" tip={"Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100"} /></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Loading…</td></tr>
                ) : topCampaigns.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>No campaign data for this period.</td></tr>
                ) : topCampaigns.map(c => (
                  <tr key={c.name} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '10px', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{(c.sent ?? 0).toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(c.delivered_pct)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(c.open_pct)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(c.click_pct)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(c.reply_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Performance by Segment</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isLoading ? (
              <p style={{ color: 'var(--gray-400)', fontSize: 13, margin: 0 }}>Loading…</p>
            ) : topSegments.length === 0 ? (
              <p style={{ color: 'var(--gray-400)', fontSize: 13, margin: 0 }}>No segment data for this period.</p>
            ) : topSegments.map(seg => (
              <div key={seg.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ fontWeight: 500 }}>{seg.name}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{seg.reply_pct}% Reply</span>
                   {/* {seg.open_pct}% Not Replied Yet · */}
                </div>
                <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(seg.reply_pct, 100)}%`, background: 'var(--sky)', borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      

      {/* Row: Sequence Performance + Sequence Funnel */}
      <div className="an-grid">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8 }}><GitBranch size={18} style={{ color: 'var(--sky)' }} /> Sequence Performance</h3>
            <Link to="/app/sequences" style={{ fontSize: 13, color: 'var(--sky)', textDecoration: 'none' }}>View All</Link>
          </div>
          <div className="an-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 500 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Sequence</th>
                  <th style={{ textAlign: 'center', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Active" tip="Number of leads currently enrolled and progressing through this sequence." /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Not Replied Yet %" tip={"Percentage of delivered emails in this sequence that were read but not yet replied to.\nFormula: (Delivered − Replied) ÷ Delivered × 100"} /></th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}><ColTip label="Reply %" tip={"Percentage of delivered emails in this sequence that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100"} /></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Loading…</td></tr>
                ) : topSequences.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>No sequence data for this period.</td></tr>
                ) : topSequences.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '10px', fontWeight: 500 }}>
                      <Link to={`/app/sequences/${s.id}`} style={{ color: 'var(--navy)', textDecoration: 'none' }}>{s.name}</Link>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-amber'}`}>
                        {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{s.active_count}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(s.open_pct)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtPct(s.reply_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 20 }}>Sequence Funnel</h3>
          {isLoading ? (
            <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Loading…</p>
          ) : !sequenceFunnel || sequenceFunnel.enrolled === 0 ? (
            <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>No funnel data for this period.</p>
          ) : (() => {
            const base = Math.max(1, sequenceFunnel.enrolled);
            const stages = [
              { label: 'Enrolled',    count: sequenceFunnel.enrolled   ?? 0, color: 'var(--navy)' },
              { label: 'Step 1 Sent', count: sequenceFunnel.step1_sent ?? 0, color: 'var(--sky)' },
              { label: 'Not Replied Yet', count: sequenceFunnel.not_replied ?? 0, color: 'var(--amber)' },
              { label: 'Replied',     count: sequenceFunnel.replied    ?? 0, color: 'var(--green)' },
            ];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {stages.map(stage => {
                  const pct = Math.min(100, Math.round(stage.count / base * 100));
                  return (
                    <div key={stage.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                        <span style={{ fontWeight: 500 }}>{stage.label}</span>
                        <span style={{ color: 'var(--gray-500)' }}>{stage.count.toLocaleString()} ({pct}%)</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: stage.color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Row: A/B Tests + Delivery Health */}
      <div className="an-grid">
        <div className="card d-none">
          <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>A/B Tests</h3>
          <div className="an-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 460 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Test</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Variant A</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Variant B</th>
                  <th style={{ textAlign: 'center', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Winner</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--gray-500)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Loading…</td></tr>
                ) : abTests.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>No A/B test data for this period.</td></tr>
                ) : abTests.map((t, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 500 }}>{t.test_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{t.sequence_name}</div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{t.variant_a_rate}% {t.metric}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--gray-500)' }}>{t.variant_b_rate}% {t.metric}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {t.winner ? (
                        <span className="badge badge-green">{t.winner}</span>
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 500 }}>{t.confidence}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Delivery Health</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <svg viewBox="0 0 200 200" style={{ width: 180, height: 180 }}>
              <circle cx="100" cy="100" r="80" fill="none" stroke="var(--gray-100)" strokeWidth="20" />
              {isLoading || deliveredLen === 0 ? null : (
                <>
                  <circle cx="100" cy="100" r="80" fill="none" stroke="var(--green)" strokeWidth="20"
                    strokeDasharray={`${deliveredLen} ${CIRC}`} strokeDashoffset="0"
                    transform="rotate(-90 100 100)" />
                  <circle cx="100" cy="100" r="80" fill="none" stroke="var(--amber)" strokeWidth="20"
                    strokeDasharray={`${softBounceLen} ${CIRC}`} strokeDashoffset={`${-deliveredLen}`}
                    transform="rotate(-90 100 100)" />
                  <circle cx="100" cy="100" r="80" fill="none" stroke="var(--red)" strokeWidth="20"
                    strokeDasharray={`${hardBounceLen} ${CIRC}`} strokeDashoffset={`${-(deliveredLen + softBounceLen)}`}
                    transform="rotate(-90 100 100)" />
                </>
              )}
              <text x="100" y="95" textAnchor="middle" fontSize="28" fontWeight="700" fill="var(--navy)">
                {isLoading ? '—' : `${deliveredPct}%`}
              </text>
              <text x="100" y="115" textAnchor="middle" fontSize="12" fill="var(--gray-500)">Delivered</text>
            </svg>
          </div>
          <div className="an-legend">
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--green)', display: 'inline-block' }} /> Delivered {isLoading ? '—' : `${deliveredPct}%`}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--amber)', display: 'inline-block' }} /> Soft Bounce {isLoading ? '—' : `${softBouncePct}%`}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--red)', display: 'inline-block' }} /> Hard Bounce {isLoading ? '—' : `${hardBouncePct}%`}</span>
          </div>
        </div>
      </div>

      {/* Best Sending Times heat map — full width */}
      <div className="card an-bst">
        <h3 className="an-bst__title">Best Sending Times</h3>
        {bstLoading ? (
          <p className="an-bst__empty">Loading…</p>
        ) : !bestSendingTimes || bestSendingTimes.slots.length === 0 ? (
          <p className="an-bst__empty">No sending time data for this period.</p>
        ) : (
          <div className="an-bst__scroll">
            <table className="an-bst__table">
              <thead>
                <tr>
                  <th className="an-bst__th" />
                  {bestSendingTimes.days.map(day => (
                    <th key={day} className="an-bst__th-day">{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bestSendingTimes.slots.map(slot => (
                  <tr key={slot.hour}>
                    <td className="an-bst__td-label">{slot.label}</td>
                    {bestSendingTimes.days.map(day => {
                      const pct = bestSendingTimes.grid[day]?.[String(slot.hour)] ?? null;
                      const { bg, text } = heatColor(pct);
                      return (
                        <td
                          key={day}
                          className={`an-bst__cell${pct !== null && pct >= 65 ? ' an-bst__cell--strong' : ''}`}
                          style={{ background: bg, color: text }}
                        >
                          {pct !== null ? `${pct}%` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="an-bst__legend">
              <span>Low</span>
              {['#d0e8fc', '#94c5f7', '#5ba3ef', '#2e7de8', '#1e5fcc'].map(c => (
                <span key={c} className="an-bst__swatch" style={{ background: c }} />
              ))}
              <span>High open rate</span>
            </div>
          </div>
        )}
      </div>
      </>
      )}
      </>
      )}

    </div>
    </TooltipProvider>
  );
}
